import * as React from "react";
import {
  AlertTriangle,
  Download,
  ExternalLink,
  FileText,
  LoaderCircle,
  LockKeyhole,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  getDocumentDownload,
  getDocumentPreview,
  type ChatSource,
  type DownloadResponse,
  type PreviewResponse,
} from "@/lib/api";

type PreviewReady = Extract<PreviewResponse, { preview_url: string }>;
type PreviewRestricted = Extract<PreviewResponse, { access_level: "restricted" }>;
type DownloadRestricted = Extract<DownloadResponse, { access_level: "restricted" }>;

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: PreviewReady }
  | { status: "restricted"; data: PreviewRestricted }
  | { status: "error"; message: string };

export type DocumentPreviewLoader = (source: ChatSource) => Promise<PreviewResponse>;
export type DocumentDownloadLoader = (source: ChatSource) => Promise<DownloadResponse>;

export const defaultDocumentPreviewLoader: DocumentPreviewLoader = (source) =>
  getDocumentPreview(source.document_id);

export const defaultDocumentDownloadLoader: DocumentDownloadLoader = (source) =>
  getDocumentDownload(source.document_id);

function formatBytes(bytes?: number): string {
  if (!bytes || bytes < 1024) return `${bytes ?? 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function sourceMeta(source: ChatSource): string {
  const parts = [source.file_type?.toUpperCase(), source.file_size ? formatBytes(source.file_size) : null]
    .filter(Boolean);
  return parts.join(" / ");
}

function isRestrictedPreview(data: PreviewResponse): data is PreviewRestricted {
  return "access_level" in data && data.access_level === "restricted";
}

function isRestrictedDownload(data: DownloadResponse): data is DownloadRestricted {
  return "access_level" in data && data.access_level === "restricted";
}

function canRenderInline(source: ChatSource, previewUrl: string): boolean {
  const fileType = (source.file_type || "").toLowerCase();
  if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileType)) return false;
  if (previewUrl.startsWith("data:text/")) return true;
  return ["pdf", "md", "markdown", "txt", "csv", "html"].includes(fileType);
}

function triggerDownload(url: string, documentName: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = documentName;
  a.target = "_blank";
  a.rel = "noopener";
  a.click();
}

function mailtoFor(source: ChatSource, requestEmail?: string | null): string {
  const email = requestEmail || source.request_access_email || "ir@greenpointpartners.com";
  const subject = encodeURIComponent(
    `Investor Q&A - request access to "${source.document_name}"`,
  );
  return `mailto:${email}?subject=${subject}`;
}

export function SourceDocumentLightbox({
  source,
  open,
  onOpenChange,
  loadPreview = defaultDocumentPreviewLoader,
  loadDownload = defaultDocumentDownloadLoader,
}: {
  source: ChatSource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loadPreview?: DocumentPreviewLoader;
  loadDownload?: DocumentDownloadLoader;
}) {
  const [previewState, setPreviewState] = React.useState<PreviewState>({ status: "idle" });
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open || !source) {
      setPreviewState({ status: "idle" });
      setDownloadError(null);
      return;
    }

    if (source.access_level === "restricted") {
      setPreviewState({
        status: "restricted",
        data: {
          error: "Document access restricted",
          access_level: "restricted",
          document_name: source.document_name,
          sensitivity: source.sensitivity ?? 5,
          request_access_email: source.request_access_email || "ir@greenpointpartners.com",
          message: `This source is available from GreenPoint IR by request.`,
        },
      });
      return;
    }

    let cancelled = false;
    setPreviewState({ status: "loading" });
    setDownloadError(null);
    void loadPreview(source)
      .then((data) => {
        if (cancelled) return;
        if ("preview_url" in data && data.preview_url) {
          setPreviewState({ status: "ready", data });
        } else if (isRestrictedPreview(data)) {
          setPreviewState({ status: "restricted", data });
        } else if ("error" in data && data.error) {
          setPreviewState({ status: "error", message: data.message || data.error });
        } else {
          setPreviewState({ status: "error", message: "Could not generate a document preview." });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewState({
          status: "error",
          message: err instanceof Error ? err.message : "Preview failed.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [loadPreview, open, source]);

  React.useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onOpenChange, open]);

  const handleDownload = React.useCallback(async () => {
    if (!source || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const data = await loadDownload(source);
      if ("download_url" in data && data.download_url) {
        triggerDownload(data.download_url, source.document_name);
      } else if (isRestrictedDownload(data)) {
        window.location.href = mailtoFor(source, data.request_access_email);
      } else {
        setDownloadError("Could not generate download URL.");
      }
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setDownloading(false);
    }
  }, [downloading, loadDownload, source]);

  if (!open || !source) return null;

  const meta = sourceMeta(source);
  const previewUrl = previewState.status === "ready" ? previewState.data.preview_url : null;
  const restrictedEmail =
    previewState.status === "restricted"
      ? previewState.data.request_access_email
      : source.request_access_email;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="source-lightbox">
      <button
        type="button"
        aria-label="Close document preview backdrop"
        className="absolute inset-0 border-0 bg-slate-950/45"
        onClick={() => onOpenChange(false)}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="source-lightbox-shell relative z-10 flex flex-col overflow-hidden rounded-md border bg-card text-card-foreground shadow-2xl"
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
          <div className="flex min-w-0 items-start gap-3">
            <Badge variant={source.access_level === "restricted" ? "warning" : "success"}>
              {source.index}
            </Badge>
            <div className="min-w-0">
              <h2 id={titleId} className="break-words text-base font-semibold leading-6">
                {source.document_name}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {meta ? <span>{meta}</span> : null}
                {source.section ? <span>{source.section}</span> : null}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {previewUrl ? (
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
              >
                <ExternalLink />
                <span>Open</span>
              </a>
            ) : null}
            {source.access_level !== "restricted" ? (
              <Button type="button" variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
                <Download />
                <span>{downloading ? "Opening..." : "Download"}</span>
              </Button>
            ) : null}
            <Button
              ref={closeRef}
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Close document preview"
              onClick={() => onOpenChange(false)}
            >
              <X />
            </Button>
          </div>
        </div>

        <div
          className="source-lightbox-frame bg-background"
          data-variant={
            previewState.status === "loading" ||
            (previewState.status === "ready" && previewUrl && canRenderInline(source, previewUrl))
              ? "inline"
              : "compact"
          }
        >
          {previewState.status === "loading" ? (
            <div className="grid h-full place-items-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <LoaderCircle className="size-4 animate-spin" />
                Loading preview
              </span>
            </div>
          ) : previewState.status === "ready" && previewUrl ? (
            canRenderInline(source, previewUrl) ? (
              <iframe
                src={previewUrl}
                title={`Preview of ${source.document_name}`}
                className="h-full w-full border-0 bg-white"
              />
            ) : (
              <div className="flex flex-col items-center px-6 py-10 text-center">
                <FileText className="size-10 text-muted-foreground" />
                <h3 className="mt-3 text-sm font-semibold">
                  {source.file_type ? `${source.file_type.toUpperCase()} preview` : "Document preview"}
                </h3>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  This file type cannot be rendered directly in the browser lightbox.
                </p>
                {source.excerpt ? (
                  <blockquote className="mt-4 max-w-xl rounded-md border bg-card p-4 text-left text-sm leading-6 text-muted-foreground">
                    {source.excerpt}
                  </blockquote>
                ) : null}
              </div>
            )
          ) : previewState.status === "restricted" ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <LockKeyhole className="size-10 text-warning" />
              <h3 className="mt-3 text-sm font-semibold">Available by request</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                {previewState.data.message}
              </p>
              <a
                href={mailtoFor(source, restrictedEmail)}
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "mt-4 border-warning/30 bg-warning-soft text-warning hover:border-warning hover:bg-warning-soft/80",
                )}
              >
                <LockKeyhole />
                <span>Request access</span>
              </a>
            </div>
          ) : previewState.status === "error" ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <AlertTriangle className="size-10 text-destructive" />
              <h3 className="mt-3 text-sm font-semibold">Preview unavailable</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">{previewState.message}</p>
            </div>
          ) : null}
        </div>

        {source.referenced_in && source.referenced_in.length > 0 ? (
          <div className="border-t bg-card px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Cited in
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {source.referenced_in.map((sec) => (
                <span
                  key={sec}
                  className="inline-flex max-w-full items-center rounded-md border bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground"
                  title={sec}
                >
                  <span className="truncate">{sec}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {downloadError ? (
          <div className="border-t px-4 py-2 text-xs text-destructive">{downloadError}</div>
        ) : null}
      </section>
    </div>
  );
}
